import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { buildSvgGuides, excludeSuppressedGuides } from './PdfGuideExtract';
import type { Field } from './constants';

export type AutoFrame = { x0: number; y0: number; x1: number; y1: number };

export function useAutoFrames(params: {
  svgElement: SVGSVGElement | null;
  formEl: HTMLElement | null;
  fields: Field[];
  imgSize: { width: number; height: number };
  minWidth?: number;
  minHeight?: number;
  suppressedGuides?: { x: number[]; y: number[] };
}) {
  const { svgElement, formEl, fields, imgSize, minWidth = 30, minHeight = 3, suppressedGuides } = params;

  const [enabled, setEnabled] = useState(false);
  const [frames, setFrames] = useState<AutoFrame[]>([]);
  const [selectedFrameIdxs, setSelectedFrameIdxs] = useState<Set<number>>(new Set());
  // Persistently suppressed frames (so they stay removed across recomputes)
  const suppressedRef = useRef<AutoFrame[]>([]);

  useEffect(() => {
    if (enabled) return;
    suppressedRef.current = [];
    setFrames([]);
    setSelectedFrameIdxs(new Set());
  }, [enabled]);

  const recompute = useCallback(() => {
    if (!svgElement || !formEl) {
      setFrames([]);
      setSelectedFrameIdxs(new Set());
      return;
    }
    const extracted = buildSvgGuides(svgElement, formEl);
    const guides = excludeSuppressedGuides(extracted, suppressedGuides);
    const xs = guides.x;
    const ys = guides.y;
    if (!xs || !ys || xs.length < 2 || ys.length < 2) {
      setFrames([]);
      setSelectedFrameIdxs(new Set());
      return;
    }
    const firstX = (xs.find(v => v >= 0) ?? xs[0]);
    const firstY = (ys.find(v => v >= 0) ?? ys[0]);

    // Build occupied rects from SVG nodes
    const skipOccupancyCheck = true;
    const formRect = formEl.getBoundingClientRect();
    let scale = 1;
    try {
      const tr = getComputedStyle(formEl).transform;
      if (tr && tr !== 'none') {
        if (tr.startsWith('matrix3d(')) {
          const nums = tr.slice(9, -1).split(',').map(Number);
          const a = nums[0];
          const d = nums[5];
          scale = !isNaN(a) ? a : (!isNaN(d) ? d : 1);
        } else if (tr.startsWith('matrix(')) {
          const nums = tr.slice(7, -1).split(',').map(Number);
          const a = nums[0];
          const d = nums[3];
          scale = !isNaN(a) ? a : (!isNaN(d) ? d : 1);
        }
      }
    } catch {}
    const occupiedRects: Array<{ left: number; right: number; top: number; bottom: number }> = [];
    try {
      // Ignore non-structural elements (text/images/foreignObject/use) so logos don't block frames
      const shapeSelector = 'line, rect, path, polyline, polygon, circle, ellipse';
      const nodes = (svgElement as SVGSVGElement).querySelectorAll(shapeSelector);
      const ignoreLogoSelector = '[id*=logo i], [class*=logo i]';
      nodes.forEach((el) => {
        if ((el as Element).closest('defs')) return;
        try { if ((el as Element).matches(ignoreLogoSelector) || (el as Element).closest(ignoreLogoSelector)) return; } catch {}
        const cs = window.getComputedStyle(el as Element);
        if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity || '1') === 0) return;
        const g = el as unknown as SVGGraphicsElement;
        const r = g.getBoundingClientRect();
        const left = Math.round((r.left - formRect.left) / scale);
        const right = Math.round((r.right - formRect.left) / scale);
        const top = Math.round((r.top - formRect.top) / scale);
        const bottom = Math.round((r.bottom - formRect.top) / scale);
        const w = right - left;
        const h = bottom - top;
        if (w <= 0 || h <= 0) return;
        occupiedRects.push({ left, right, top, bottom });
      });
      // Filter out overly large occupied rects (likely headers/footers/logos) so they don't blanket-reject frames
      const pageArea = Math.max(1, (imgSize?.width || 1) * (imgSize?.height || 1));
      const maxAreaRatio = 0.15; // ignore shapes covering >15% of the page
      const maxHeightRatio = 0.20; // ignore shapes taller than 20% of the page height
      for (let i = occupiedRects.length - 1; i >= 0; i--) {
        const or = occupiedRects[i];
        const area = Math.max(0, (or.right - or.left) * (or.bottom - or.top));
        const heightRatio = Math.max(0, (or.bottom - or.top)) / Math.max(1, imgSize?.height || 1);
        if ((area / pageArea) > maxAreaRatio || heightRatio > maxHeightRatio) {
          occupiedRects.splice(i, 1);
        }
      }
    } catch {}

    let nextFrames: AutoFrame[] = [];
    for (let yi = 0; yi < ys.length - 1; yi++) {
      for (let xi = 0; xi < xs.length - 1; xi++) {
        const x0 = xs[xi];
        const x1 = xs[xi + 1];
        const y0 = ys[yi];
        const y1 = ys[yi + 1];
        const w = x1 - x0;
        const h = y1 - y0;
        if (w < minWidth || h < minHeight) continue;
        if (y0 < firstY || x0 < firstX) continue;
        // Skip if inner area intersects any occupied rect
        const inset = 4; // slightly larger inset to avoid edge touches
        const ix0 = x0 + inset;
        const iy0 = y0 + inset;
        const ix1 = x1 - inset;
        const iy1 = y1 - inset;
        if (ix1 - ix0 <= 0 || iy1 - iy0 <= 0) continue;
        const candidateArea = (ix1 - ix0) * (iy1 - iy0);
        const overlapRatioThreshold = 0.20; // 20% of candidate area
        const intersectsOccupied = skipOccupancyCheck ? false : occupiedRects.some(or => {
          const iw = Math.min(ix1, or.right) - Math.max(ix0, or.left);
          const ih = Math.min(iy1, or.bottom) - Math.max(iy0, or.top);
          const area = (iw > 0 && ih > 0) ? (iw * ih) : 0;
          return candidateArea > 0 && (area / candidateArea) > overlapRatioThreshold;
        });
        if (intersectsOccupied) continue;
        // Exclude overlap with existing fields
        const overlapsField = (fields || []).some(f => {
          const p = f.position; if (!p) return false;
          const ox0 = Math.max(x0, p.x0);
          const oy0 = Math.max(y0, p.y0);
          const ox1 = Math.min(x1, p.x1);
          const oy1 = Math.min(y1, p.y1);
          return (ox1 - ox0) > 0 && (oy1 - oy0) > 0;
        });
        if (overlapsField) continue;
        nextFrames.push({ x0, y0, x1, y1 });
      }
    }
    // Filter out suppressed frames (approximate equality within epsilon)
    const eps = 1.5;
    const isSame = (a: AutoFrame, b: AutoFrame) => Math.abs(a.x0 - b.x0) <= eps && Math.abs(a.y0 - b.y0) <= eps && Math.abs(a.x1 - b.x1) <= eps && Math.abs(a.y1 - b.y1) <= eps;
    if (suppressedRef.current.length > 0) {
      nextFrames = nextFrames.filter(f => !suppressedRef.current.some(s => isSame(f, s)));
    }
    setFrames(nextFrames);
    setSelectedFrameIdxs(new Set());
    if (nextFrames.length === 0) {
      message.info('No candidate frames detected.');
    }
  }, [svgElement, formEl, fields, minWidth, minHeight, suppressedGuides]);

  useEffect(() => {
    if (!enabled) return;
    recompute();
  }, [enabled, imgSize.width, imgSize.height, fields, svgElement, recompute]);

  const frameLikeFields = useMemo(() => frames.map(r => ({ position: { x0: r.x0, y0: r.y0, x1: r.x1, y1: r.y1 } })), [frames]);

  return {
    enabled,
    setEnabled,
    frames,
    selectedFrameIdxs,
    setSelectedFrameIdxs,
    frameLikeFields,
    recompute,
    removeFramesByIdxs: (idxs: Set<number>) => {
      if (!idxs || idxs.size === 0) return;
      setFrames(prev => {
        const removed: AutoFrame[] = [];
        const kept = prev.filter((f, i) => {
          const drop = idxs.has(i);
          if (drop) removed.push(f);
          return !drop;
        });
        // Merge removed into suppressed list, avoiding duplicates
        const eps = 1.5;
        const isSame = (a: AutoFrame, b: AutoFrame) => Math.abs(a.x0 - b.x0) <= eps && Math.abs(a.y0 - b.y0) <= eps && Math.abs(a.x1 - b.x1) <= eps && Math.abs(a.y1 - b.y1) <= eps;
        const sup = suppressedRef.current.slice();
        removed.forEach(r => { if (!sup.some(s => isSame(s, r))) sup.push(r); });
        suppressedRef.current = sup;
        return kept;
      });
      setSelectedFrameIdxs(new Set());
    },
  } as const;
}


